import { ExternalResource } from '../../helpers/helpers';
import { SearchCategory } from '../vacancy-service/vacancy-helpers.interface';

export enum EvaluationDecision {
  YES = 'YES',
  NO = 'NO',
}

/**
 * AI-ready vacancy data, assembled by vacancy-service from the vacancy and its
 * custom fields. Locations are plain strings (city ids are useless to the LLM and
 * the exact location may live in a custom field).
 */
export interface VacancyEvaluationData {
  additionalInformation: string | null;
  budgetMax: number | null;
  currency: string | null;
  description: string | null;
  extraInfoForAI: string;
  id: number;
  location: string | null;
  skills: string[];
  title: string;
  vacancyType: SearchCategory | null;
}

export interface PreviousApplication {
  vacancy: {
    id: number;
    title: string;
  };
  status: {
    id: number;
    name: string;
  };
  rejectReason: string | null;
  comments: string[] | null;
}

interface BaseCandidateEvaluationInput {
  additionalInformation: string | null;
  cvUrl: string | null;
  experience: string | null;
  fullName: string;
  location: string | null;
  salaryExpectation: number | null;
  skills: string[];
  summary: string | null;
}

/**
 * Internal candidate (exists in our DB). DB fields are frequently empty/outdated,
 * so `cvUrl` is included and the prompt prefers CV content where structured data is sparse.
 */
export interface InternalCandidateEvaluationInput extends BaseCandidateEvaluationInput {
  comments: string[];
  previousApplications: PreviousApplication[];
  profileId: number;
  salaryComment: string | null;
}

/**
 * External candidate (e.g. RobotaUa recommendation). Not materialized in our DB:
 * identified by source + external application id; CV available as a URL/resume only.
 */
export interface ExternalCandidateEvaluationInput extends BaseCandidateEvaluationInput {
  externalApplicationId: string;
  relevanceScore: number | null;
  resumeId: number;
  source: ExternalResource;
}

/** Structured result returned by the evaluate_candidate tool. */
export interface CandidateEvaluation {
  decision: EvaluationDecision;
  score: number;
  reasoning: string;
  strengths: string[];
  concerns: string[];
  commentForRecruiter: string;
  budgetFlag: boolean;
  locationFlag: boolean;
}

/**
 * A persisted AI evaluation as shown on a candidate/profile card (separate block,
 * not mixed into recruiter comments). One per vacancy the agent evaluated.
 */
export interface ProfileAiEvaluation {
  id: number;
  vacancyId: number;
  decision: EvaluationDecision;
  score: number;
  reasoning: string;
  strengths: string[];
  concerns: string[];
  commentForRecruiter: string;
  budgetFlag: boolean;
  locationFlag: boolean;
  createdAt: string;
}

/** Input for creating the recruiter-facing comment on an AI-created shortlist. */
export interface ShortlistCommentInput {
  bidId: number;
  profileId: number;
  projectId: number;
  portalId: number;
  addedById: number;
  bidStatusId: number;
  comment: string;
}
